import { brandColor } from '../../../_helpers/brand-color';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription, skip } from 'rxjs';
import { ChartData, ChartOptions } from 'chart.js';

import { BaseComponent } from '../../../components/base/base.component';
import { SessionService } from '../../../_services/session/session.service';
import { StatsFilterService } from '../statistics-filter.service';
import {
  StatsDataService,
  AppointmentMetrics,
  HourlyData,
  CategoryData,
  AppointmentsHeatmapData,
} from '../statistics-data.service';

// Components
import { KpiCardComponent } from '../_components/kpi-card/kpi-card.component';
import { StatChartComponent } from '../_components/stat-chart/stat-chart.component';

@Component({
  standalone: true,
  selector: 'app-stats-appointments',
  templateUrl: './appointments.component.html',
  styleUrls: ['./appointments.component.scss'],
  imports: [
    CommonModule,
    TranslateModule,
    KpiCardComponent,
    StatChartComponent,
  ],
})
export class StatsAppointmentsComponent
  extends BaseComponent
  implements OnInit, OnDestroy
{
  private subscriptions: Subscription[] = [];

  // Loading
  public loadingMetrics: boolean = true;
  public loadingHourly: boolean = true;
  public loadingDaily: boolean = true;
  public loadingStatus: boolean = true;
  public loadingHeatmap: boolean = true;

  // Data
  public metrics: AppointmentMetrics | null = null;
  public hourlyData: HourlyData[] = [];
  public dailyData: CategoryData[] = [];
  public statusData: CategoryData[] = [];

  // Charts
  public hourlyChartData: ChartData | null = null;
  public hourlyChartOptions: ChartOptions = {};
  public dailyChartData: ChartData | null = null;
  public dailyChartOptions: ChartOptions = {};
  public statusChartData: ChartData | null = null;
  public statusChartOptions: ChartOptions = {};
  public heatmapData: number[][] = [];
  public heatmapHours: string[] = [];
  public heatmapDays: string[] = [];
  private rawHeatmapData: AppointmentsHeatmapData | null = null;
  public heatmapPeakSlot:
    | {
        dayName: string;
        hour: number;
        count: number;
      }
    | null = null;

  constructor(
    public session: SessionService,
    private filterService: StatsFilterService,
    private dataService: StatsDataService,
    private translate: TranslateService,
  ) {
    super(session);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.loadData();

    this.subscriptions.push(
      this.filterService.filters$
        .pipe(skip(1))
        .subscribe(() => this.loadData()),
    );

    this.subscriptions.push(
      this.translate.onLangChange.subscribe(() => {
        if (this.hourlyData.length) this.buildHourlyChart();
        if (this.dailyData.length) this.buildDailyChart();
        if (this.statusData.length) this.buildStatusChart();
        this.buildHeatmap(this.rawHeatmapData);
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  loadData(): void {
    const customerId = this.session.getCustomer()?.id;
    const siteId = this.session.getSite()?.id;

    if (!customerId || !siteId) return;

    // Metrics
    this.loadingMetrics = true;
    this.dataService.getAppointmentMetrics(customerId, siteId).subscribe({
      next: (data) => {
        this.metrics = data;
        this.loadingMetrics = false;
      },
      error: () => (this.loadingMetrics = false),
    });

    // Hourly distribution
    this.loadingHourly = true;
    this.dataService.getAppointmentsByHour(customerId, siteId).subscribe({
      next: (data) => {
        this.hourlyData = data;
        this.buildHourlyChart();
        this.loadingHourly = false;
      },
      error: () => (this.loadingHourly = false),
    });

    // Daily distribution
    this.loadingDaily = true;
    this.dataService.getAppointmentsByDay(customerId, siteId).subscribe({
      next: (data) => {
        this.dailyData = data;
        this.buildDailyChart();
        this.loadingDaily = false;
      },
      error: () => (this.loadingDaily = false),
    });

    // Status distribution
    this.loadingStatus = true;
    this.dataService.getAppointmentsByStatus(customerId, siteId).subscribe({
      next: (data) => {
        this.statusData = data;
        this.buildStatusChart();
        this.loadingStatus = false;
      },
      error: () => (this.loadingStatus = false),
    });

    // Heatmap
    this.loadingHeatmap = true;
    this.dataService.getAppointmentsHeatmap(customerId, siteId).subscribe({
      next: (data) => {
        this.buildHeatmap(data);
        this.loadingHeatmap = false;
      },
      error: () => (this.loadingHeatmap = false),
    });
  }

  private buildHourlyChart(): void {
    this.hourlyChartData = {
      labels: this.hourlyData.map((h) => h.hour + ':00'),
      datasets: [
        {
          label: this.translate.instant(
            'STATISTICS.APPOINTMENTS.CHARTS.HOURLY_DATASET',
          ),
          data: this.hourlyData.map((h) => h.count),
          backgroundColor: brandColor(),
          borderRadius: 4,
        },
      ],
    };

    this.hourlyChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: 'y',
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: '#1e293b',
          padding: 12,
          cornerRadius: 8,
        },
      },
      scales: {
        x: {
          grid: { color: '#f1f5f9' },
          ticks: { color: '#64748b', font: { size: 11 } },
          beginAtZero: true,
        },
        y: {
          grid: { display: false },
          ticks: { color: '#64748b', font: { size: 11 } },
        },
      },
    };
  }

  private buildDailyChart(): void {
    this.dailyChartData = {
      labels: this.dailyData.map((d) => this.translateDayName(d.name)),
      datasets: [
        {
          label: this.translate.instant(
            'STATISTICS.APPOINTMENTS.CHARTS.DAILY_DATASET',
          ),
          data: this.dailyData.map((d) => d.count),
          backgroundColor: [
            brandColor(),
            '#8b5cf6',
            '#10b981',
            '#f59e0b',
            '#ef4444',
            '#6366f1',
            '#14b8a6',
          ],
          borderRadius: 4,
        },
      ],
    };

    this.dailyChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: '#1e293b',
          padding: 12,
          cornerRadius: 8,
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { color: '#64748b', font: { size: 11 } },
        },
        y: {
          grid: { color: '#f1f5f9' },
          ticks: { color: '#64748b', font: { size: 11 } },
          beginAtZero: true,
        },
      },
    };
  }

  private buildStatusChart(): void {
    this.statusChartData = {
      labels: this.statusData.map((s) => this.translateStatusName(s.name)),
      datasets: [
        {
          data: this.statusData.map((s) => s.count),
          backgroundColor: this.statusData.map((s) => s.color || '#94a3b8'),
          borderWidth: 0,
        },
      ],
    };

    this.statusChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: 'y',
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: '#1e293b',
          padding: 12,
          cornerRadius: 8,
        },
      },
      scales: {
        x: {
          grid: { color: '#f1f5f9' },
          ticks: { color: '#64748b', font: { size: 11 } },
          beginAtZero: true,
        },
        y: {
          grid: { display: false },
          ticks: { color: '#64748b', font: { size: 11 } },
        },
      },
    };
  }

  private buildHeatmap(data: AppointmentsHeatmapData | null): void {
    this.rawHeatmapData = data;

    if (!data) {
      this.heatmapHours = [];
      this.heatmapDays = [];
      this.heatmapData = [];
      this.heatmapPeakSlot = null;
      return;
    }

    this.heatmapHours = (data.hours || []).map((hour) => `${hour}:00`);
    this.heatmapDays = (data.days || []).map((day) =>
      this.translateDayName(day.dayName, true),
    );
    this.heatmapData = data.matrix || [];
    this.heatmapPeakSlot = data.peakSlot
      ? {
          ...data.peakSlot,
          dayName: this.translateDayName(data.peakSlot.dayName),
        }
      : null;
  }

  getHeatmapCellClass(value: number): string {
    if (value >= 10) return 'heatmap-cell--very-high';
    if (value >= 7) return 'heatmap-cell--high';
    if (value >= 4) return 'heatmap-cell--medium';
    if (value >= 1) return 'heatmap-cell--low';
    return 'heatmap-cell--none';
  }

  getNoShowRateClass(): string {
    if (!this.metrics) return '';
    if (this.metrics.noShowRate > 15) return 'kpi--danger';
    if (this.metrics.noShowRate > 10) return 'kpi--warning';
    return 'kpi--success';
  }

  getOccupancyClass(): string {
    if (!this.metrics) return '';
    if (this.metrics.occupancyRate >= 80) return 'kpi--success';
    if (this.metrics.occupancyRate >= 60) return 'kpi--warning';
    return 'kpi--danger';
  }

  getPeakHourLabel(): string {
    if (!this.hourlyData.length)
      return this.translate.instant('STATISTICS.GENERAL.NOT_AVAILABLE');
    const peakHour = [...this.hourlyData].sort((a, b) => b.count - a.count)[0];
    return `${peakHour.hour}:00`;
  }

  getBusiestDayLabel(): string {
    if (!this.dailyData.length)
      return this.translate.instant('STATISTICS.GENERAL.NOT_AVAILABLE');
    const busiestDay = [...this.dailyData].sort((a, b) => b.count - a.count)[0];
    return this.translateDayName(busiestDay.name);
  }

  getHeatmapPeakLabel(): string {
    if (!this.heatmapPeakSlot)
      return this.translate.instant('STATISTICS.GENERAL.NOT_AVAILABLE');
    return `${this.heatmapPeakSlot.dayName} ${this.heatmapPeakSlot.hour}:00`;
  }

  private translateStatusName(statusName: string): string {
    const normalized = this.normalize(statusName);
    const statusMap: Record<string, string> = {
      completed: 'STATUS.COMPLETED',
      completada: 'STATUS.COMPLETED',
      completado: 'STATUS.COMPLETED',
      confirmed: 'STATUS.CONFIRMED',
      confirmada: 'STATUS.CONFIRMED',
      confirmat: 'STATUS.CONFIRMED',
      pending: 'STATUS.TO_BE_CONFIRMED',
      pendent: 'STATUS.TO_BE_CONFIRMED',
      to_be_confirmed: 'STATUS.TO_BE_CONFIRMED',
      cancelled: 'STATUS.CANCELED',
      canceled: 'STATUS.CANCELED',
      cancelada: 'STATUS.CANCELED',
      cancelado: 'STATUS.CANCELED',
      no_show: 'STATISTICS.APPOINTMENTS.STATUS.NO_SHOW',
      noshow: 'STATISTICS.APPOINTMENTS.STATUS.NO_SHOW',
    };

    const key = statusMap[normalized] || statusMap[normalized.replace(/-/g, '_')];
    return key ? this.translate.instant(key) : statusName;
  }

  private translateDayName(dayName: string, short: boolean = false): string {
    const normalized = this.normalize(dayName);
    const dayMap: Record<string, string> = {
      monday: short ? 'STATISTICS.DAYS.SHORT.MONDAY' : 'STATISTICS.DAYS.MONDAY',
      lunes: short ? 'STATISTICS.DAYS.SHORT.MONDAY' : 'STATISTICS.DAYS.MONDAY',
      dilluns:
        short ? 'STATISTICS.DAYS.SHORT.MONDAY' : 'STATISTICS.DAYS.MONDAY',
      tuesday:
        short ? 'STATISTICS.DAYS.SHORT.TUESDAY' : 'STATISTICS.DAYS.TUESDAY',
      martes:
        short ? 'STATISTICS.DAYS.SHORT.TUESDAY' : 'STATISTICS.DAYS.TUESDAY',
      dimarts:
        short ? 'STATISTICS.DAYS.SHORT.TUESDAY' : 'STATISTICS.DAYS.TUESDAY',
      wednesday:
        short ? 'STATISTICS.DAYS.SHORT.WEDNESDAY' : 'STATISTICS.DAYS.WEDNESDAY',
      miercoles:
        short ? 'STATISTICS.DAYS.SHORT.WEDNESDAY' : 'STATISTICS.DAYS.WEDNESDAY',
      dimecres:
        short ? 'STATISTICS.DAYS.SHORT.WEDNESDAY' : 'STATISTICS.DAYS.WEDNESDAY',
      thursday:
        short ? 'STATISTICS.DAYS.SHORT.THURSDAY' : 'STATISTICS.DAYS.THURSDAY',
      jueves:
        short ? 'STATISTICS.DAYS.SHORT.THURSDAY' : 'STATISTICS.DAYS.THURSDAY',
      dijous:
        short ? 'STATISTICS.DAYS.SHORT.THURSDAY' : 'STATISTICS.DAYS.THURSDAY',
      friday: short ? 'STATISTICS.DAYS.SHORT.FRIDAY' : 'STATISTICS.DAYS.FRIDAY',
      viernes:
        short ? 'STATISTICS.DAYS.SHORT.FRIDAY' : 'STATISTICS.DAYS.FRIDAY',
      divendres:
        short ? 'STATISTICS.DAYS.SHORT.FRIDAY' : 'STATISTICS.DAYS.FRIDAY',
      saturday:
        short ? 'STATISTICS.DAYS.SHORT.SATURDAY' : 'STATISTICS.DAYS.SATURDAY',
      sabado:
        short ? 'STATISTICS.DAYS.SHORT.SATURDAY' : 'STATISTICS.DAYS.SATURDAY',
      dissabte:
        short ? 'STATISTICS.DAYS.SHORT.SATURDAY' : 'STATISTICS.DAYS.SATURDAY',
      sunday: short ? 'STATISTICS.DAYS.SHORT.SUNDAY' : 'STATISTICS.DAYS.SUNDAY',
      domingo:
        short ? 'STATISTICS.DAYS.SHORT.SUNDAY' : 'STATISTICS.DAYS.SUNDAY',
      diumenge:
        short ? 'STATISTICS.DAYS.SHORT.SUNDAY' : 'STATISTICS.DAYS.SUNDAY',
    };

    const key = dayMap[normalized];
    if (!key) return short ? dayName.slice(0, 3) : dayName;
    return this.translate.instant(key);
  }

  private normalize(value: string): string {
    return (value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, '_');
  }
}
